import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Track, type RemoteParticipant, type Room } from 'livekit-client';
import { Activity, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// In-call WebRTC diagnostics panel (task #70). Polling lives here, not in
// call-context, so a 1 Hz interval doesn't re-render every participant tile.

const POLL_INTERVAL_MS = 1000;

type ConnectionPath = 'direct' | 'relay-udp' | 'relay-tcp' | 'relay-tls' | 'unknown';

// Narrow views over the RTCStats fields we read. All optional — browser
// reports (and LiveKit wrappers) regularly omit fields between samples.
interface InboundRtpStats {
  type: 'inbound-rtp';
  codecId?: string;
  kind?: string;
  bytesReceived?: number;
  packetsReceived?: number;
  packetsLost?: number;
  jitter?: number;
  frameWidth?: number;
  frameHeight?: number;
}
interface OutboundRtpStats {
  type: 'outbound-rtp';
  codecId?: string;
  kind?: string;
  bytesSent?: number;
  frameWidth?: number;
  frameHeight?: number;
}
interface RemoteInboundRtpStats {
  type: 'remote-inbound-rtp';
  roundTripTime?: number;
}
// Receiver-side RTT lives here (remote-inbound-rtp is sender-side only).
interface RemoteOutboundRtpStats {
  type: 'remote-outbound-rtp';
  roundTripTime?: number;
}
interface CodecStats {
  type: 'codec';
  id: string;
  mimeType?: string;
}
interface CandidatePairStats {
  type: 'candidate-pair';
  state?: string;
  nominated?: boolean;
  selected?: boolean;
  localCandidateId?: string;
  remoteCandidateId?: string;
  currentRoundTripTime?: number;
  protocol?: string;
  localCandidateType?: string;
}
interface LocalCandidateStats {
  type: 'local-candidate';
  id: string;
  candidateType?: string;
  protocol?: string;
  /** TURN -> server transport, present only on relay candidates. */
  relayProtocol?: string;
}
type AnyStat =
  | InboundRtpStats
  | OutboundRtpStats
  | RemoteInboundRtpStats
  | RemoteOutboundRtpStats
  | CodecStats
  | CandidatePairStats
  | LocalCandidateStats
  | { type: string };

function isInbound(s: AnyStat): s is InboundRtpStats { return s.type === 'inbound-rtp'; }
function isOutbound(s: AnyStat): s is OutboundRtpStats { return s.type === 'outbound-rtp'; }
function isRemoteInbound(s: AnyStat): s is RemoteInboundRtpStats { return s.type === 'remote-inbound-rtp'; }
function isRemoteOutbound(s: AnyStat): s is RemoteOutboundRtpStats { return s.type === 'remote-outbound-rtp'; }
function isCodec(s: AnyStat): s is CodecStats { return s.type === 'codec'; }
function isCandidatePair(s: AnyStat): s is CandidatePairStats { return s.type === 'candidate-pair'; }
function isLocalCandidate(s: AnyStat): s is LocalCandidateStats { return s.type === 'local-candidate'; }

interface PerParticipantStats {
  identity: string;
  audio?: {
    codec?: string;
    rttMs?: number;
    jitterMs?: number;
    lossPct?: number;
    bitrateKbps?: number;
  };
  video?: {
    codec?: string;
    rttMs?: number;
    jitterMs?: number;
    lossPct?: number;
    bitrateKbps?: number;
    width?: number;
    height?: number;
  };
}

interface LocalPubStats {
  audio?: { bitrateKbps?: number; codec?: string };
  video?: {
    bitrateKbps?: number;
    codec?: string;
    width?: number;
    height?: number;
  };
}

/** Best-effort region hint from the connected URL. LiveKit Cloud's auto-routing
 *  hostname is `<project>.livekit.cloud`; region-pinned hostnames embed a
 *  region code (e.g. `production-eu`). */
function describeServerUrl(url: string | null): { host: string; region: string } {
  if (!url) return { host: '—', region: 'unknown' };
  try {
    const u = new URL(url.replace(/^ws/, 'http'));
    const host = u.host;
    if (/^[^.]+\.livekit\.cloud$/i.test(host)) return { host, region: 'global (auto)' };
    const m = host.match(/^[^.]+\.([^.]+)\.livekit\.cloud$/i);
    if (m) return { host, region: `pinned: ${m[1]}` };
    return { host, region: 'custom' };
  } catch {
    return { host: url, region: 'unknown' };
  }
}

function classifyPath(
  pair: CandidatePairStats | null,
  local: LocalCandidateStats | null,
): ConnectionPath {
  if (!pair || !local) return 'unknown';
  const candType = local.candidateType ?? pair.localCandidateType ?? '';
  const protocol = (local.protocol ?? pair.protocol ?? '').toLowerCase();
  const relayProtocol = (local.relayProtocol ?? '').toLowerCase();
  if (candType === 'relay') {
    // Prefer relayProtocol (TURN→server transport) over the local-to-TURN leg.
    const p = relayProtocol || protocol;
    if (p === 'tls') return 'relay-tls';
    if (p === 'tcp') return 'relay-tcp';
    return 'relay-udp';
  }
  if (candType === 'host' || candType === 'srflx' || candType === 'prflx') return 'direct';
  return 'unknown';
}

function pathColour(path: ConnectionPath): string {
  switch (path) {
    case 'direct': return 'text-emerald-600 dark:text-emerald-400';
    case 'relay-udp': return 'text-amber-600 dark:text-amber-400';
    case 'relay-tcp':
    case 'relay-tls': return 'text-red-600 dark:text-red-400';
    default: return 'text-muted-foreground';
  }
}

function pathDot(path: ConnectionPath): string {
  switch (path) {
    case 'direct': return 'bg-emerald-500';
    case 'relay-udp': return 'bg-amber-500';
    case 'relay-tcp':
    case 'relay-tls': return 'bg-red-500';
    default: return 'bg-muted-foreground/40';
  }
}

function pathLabel(path: ConnectionPath): string {
  switch (path) {
    case 'direct': return 'Direct';
    case 'relay-udp': return 'Relay UDP';
    case 'relay-tcp': return 'Relay TCP';
    case 'relay-tls': return 'Relay TLS';
    default: return 'Connecting…';
  }
}

function fmt(n: number | undefined, digits = 0, suffix = ''): string {
  if (n === undefined || n === null || Number.isNaN(n) || !Number.isFinite(n)) return '—';
  return `${n.toFixed(digits)}${suffix}`;
}

interface BytesSample { bytes: number; ts: number }

// LiveKit Track wraps the W3C RTCRtpReceiver/Sender; not on the public type
// surface, so we declare the minimum slice we use.
interface TrackWithStats {
  receiver?: { getStats?: () => Promise<RTCStatsReport> };
  sender?: { getStats?: () => Promise<RTCStatsReport> };
}

function forEachStat(report: RTCStatsReport, cb: (s: AnyStat) => void): void {
  report.forEach((value: AnyStat) => cb(value));
}

// Receiver-side per-stream RTT comes from remote-outbound-rtp (the remote
// sender's SR). remote-inbound-rtp is sender-side only; we still check it
// as a fallback for off-spec browsers, then fall back to candidate-pair RTT.
async function readParticipantStats(
  participant: RemoteParticipant,
  prevAudioBytes: Map<string, BytesSample>,
  prevVideoBytes: Map<string, BytesSample>,
  transportRttMs: number | undefined,
): Promise<PerParticipantStats> {
  const out: PerParticipantStats = { identity: participant.identity };

  for (const pub of Array.from(participant.trackPublications.values())) {
    const track = pub.track as TrackWithStats | undefined;
    const getStats = track?.receiver?.getStats;
    if (!track || !getStats) continue;
    let report: RTCStatsReport;
    try {
      report = await getStats.call(track.receiver);
    } catch {
      continue;
    }
    let inbound: InboundRtpStats | null = null;
    let codec: string | undefined;
    let codecId: string | undefined;
    const codecsById = new Map<string, CodecStats>();
    let remoteOutbound: RemoteOutboundRtpStats | null = null;
    let remoteInbound: RemoteInboundRtpStats | null = null;
    forEachStat(report, (s) => {
      if (isInbound(s)) { inbound = s; codecId = s.codecId; }
      else if (isCodec(s)) { codecsById.set(s.id, s); }
      else if (isRemoteOutbound(s)) { remoteOutbound = s; }
      else if (isRemoteInbound(s)) { remoteInbound = s; }
    });
    if (!inbound) continue;
    const inb = inbound as InboundRtpStats;
    const remOut = remoteOutbound as RemoteOutboundRtpStats | null;
    const remIn = remoteInbound as RemoteInboundRtpStats | null;
    if (codecId) {
      const c = codecsById.get(codecId);
      if (c?.mimeType) codec = String(c.mimeType).split('/').pop();
    }

    const lossPct = inb.packetsReceived
      ? ((inb.packetsLost ?? 0) / ((inb.packetsLost ?? 0) + (inb.packetsReceived ?? 0))) * 100
      : undefined;

    // RTT preference order: (1) per-stream sender RTT, (2) per-stream
    // receiver RTT (rare for inbound but harmless to try), (3) transport
    // RTT from the candidate-pair as a fallback.
    let rttMs: number | undefined;
    if (typeof remOut?.roundTripTime === 'number') rttMs = remOut.roundTripTime * 1000;
    else if (typeof remIn?.roundTripTime === 'number') rttMs = remIn.roundTripTime * 1000;
    else rttMs = transportRttMs;

    if (pub.kind === Track.Kind.Audio) {
      const prev = prevAudioBytes.get(pub.trackSid);
      const now = { bytes: inb.bytesReceived ?? 0, ts: Date.now() };
      let bitrateKbps: number | undefined;
      if (prev && now.ts > prev.ts) {
        const dBytes = Math.max(0, now.bytes - prev.bytes);
        bitrateKbps = (dBytes * 8) / (now.ts - prev.ts);
      }
      prevAudioBytes.set(pub.trackSid, now);
      out.audio = {
        codec,
        rttMs,
        jitterMs: typeof inb.jitter === 'number' ? inb.jitter * 1000 : undefined,
        lossPct,
        bitrateKbps,
      };
    } else if (pub.kind === Track.Kind.Video && pub.source !== Track.Source.ScreenShare) {
      const prev = prevVideoBytes.get(pub.trackSid);
      const now = { bytes: inb.bytesReceived ?? 0, ts: Date.now() };
      let bitrateKbps: number | undefined;
      if (prev && now.ts > prev.ts) {
        const dBytes = Math.max(0, now.bytes - prev.bytes);
        bitrateKbps = (dBytes * 8) / (now.ts - prev.ts);
      }
      prevVideoBytes.set(pub.trackSid, now);
      out.video = {
        codec,
        rttMs,
        jitterMs: typeof inb.jitter === 'number' ? inb.jitter * 1000 : undefined,
        lossPct,
        bitrateKbps,
        width: inb.frameWidth,
        height: inb.frameHeight,
      };
    }
  }
  return out;
}

// livekit-client doesn't publicly expose its peer connections; reach into
// the engine to get the subscriber PC for candidate-pair stats. Internal
// surface has drifted between versions — probe a few known shapes.
interface RoomEngineShim {
  url?: string;
  pcManager?: {
    subscriber?: {
      getPC?: () => RTCPeerConnection | undefined;
      pc?: RTCPeerConnection;
    };
  };
  subscriber?: { pc?: RTCPeerConnection };
}
function getEngine(room: Room): RoomEngineShim | undefined {
  return (room as unknown as { engine?: RoomEngineShim }).engine;
}

async function readSubscriberPathStats(room: Room): Promise<{ path: ConnectionPath; rttMs?: number }> {
  const engine = getEngine(room);
  const pc =
    engine?.pcManager?.subscriber?.getPC?.()
    ?? engine?.pcManager?.subscriber?.pc
    ?? engine?.subscriber?.pc;
  if (!pc || typeof pc.getStats !== 'function') return { path: 'unknown' };
  let report: RTCStatsReport;
  try {
    report = await pc.getStats();
  } catch {
    return { path: 'unknown' };
  }
  let activePair: CandidatePairStats | null = null;
  let firstSucceededPair: CandidatePairStats | null = null;
  const localById = new Map<string, LocalCandidateStats>();
  forEachStat(report, (s) => {
    if (isLocalCandidate(s)) {
      localById.set(s.id, s);
    } else if (isCandidatePair(s) && s.state === 'succeeded') {
      if ((s.nominated || s.selected) && !activePair) activePair = s;
      if (!firstSucceededPair) firstSucceededPair = s;
    }
  });
  // Fall back to any succeeded pair if nothing's nominated yet (brief ICE window).
  // Cast back to the declared union — TS can't track assignments inside forEachStat.
  const pair: CandidatePairStats | null =
    (activePair as CandidatePairStats | null) ?? (firstSucceededPair as CandidatePairStats | null);
  const local = pair?.localCandidateId ? localById.get(pair.localCandidateId) ?? null : null;
  return {
    path: classifyPath(pair, local),
    rttMs: pair?.currentRoundTripTime != null ? pair.currentRoundTripTime * 1000 : undefined,
  };
}

/** Read local publication bitrate + codec. */
async function readLocalPubStats(
  room: Room,
  prevAudio: BytesSample | null,
  prevVideo: BytesSample | null,
): Promise<{ stats: LocalPubStats; nextAudio: BytesSample | null; nextVideo: BytesSample | null }> {
  const out: LocalPubStats = {};
  let nextAudio = prevAudio;
  let nextVideo = prevVideo;
  const lp = room.localParticipant;

  for (const pub of Array.from(lp.trackPublications.values())) {
    const track = pub.track as TrackWithStats | undefined;
    const getStats = track?.sender?.getStats;
    if (!track || !getStats) continue;
    let report: RTCStatsReport;
    try { report = await getStats.call(track.sender); } catch { continue; }
    let outbound: OutboundRtpStats | null = null;
    let codec: string | undefined;
    let codecId: string | undefined;
    const codecsById = new Map<string, CodecStats>();
    forEachStat(report, (s) => {
      if (isOutbound(s)) {
        // Pick the highest-bytes layer (the one actually being delivered to most subscribers).
        if (!outbound || (s.bytesSent ?? 0) > (outbound.bytesSent ?? 0)) {
          outbound = s;
          codecId = s.codecId;
        }
      } else if (isCodec(s)) {
        codecsById.set(s.id, s);
      }
    });
    if (!outbound) continue;
    const outb = outbound as OutboundRtpStats;
    if (codecId) {
      const c = codecsById.get(codecId);
      if (c?.mimeType) codec = String(c.mimeType).split('/').pop();
    }
    const now: BytesSample = { bytes: outb.bytesSent ?? 0, ts: Date.now() };

    if (pub.kind === Track.Kind.Audio) {
      let kbps: number | undefined;
      if (prevAudio && now.ts > prevAudio.ts) {
        const dBytes = Math.max(0, now.bytes - prevAudio.bytes);
        kbps = (dBytes * 8) / (now.ts - prevAudio.ts);
      }
      out.audio = { bitrateKbps: kbps, codec };
      nextAudio = now;
    } else if (pub.kind === Track.Kind.Video && pub.source !== Track.Source.ScreenShare) {
      let kbps: number | undefined;
      if (prevVideo && now.ts > prevVideo.ts) {
        const dBytes = Math.max(0, now.bytes - prevVideo.bytes);
        kbps = (dBytes * 8) / (now.ts - prevVideo.ts);
      }
      out.video = {
        bitrateKbps: kbps,
        codec,
        width: outb.frameWidth,
        height: outb.frameHeight,
      };
      nextVideo = now;
    }
  }
  return { stats: out, nextAudio, nextVideo };
}

interface CallConnectionStatsProps {
  room: Room;
  remoteParticipants: RemoteParticipant[];
  identityToLabel?: (identity: string) => string;
  onClose: () => void;
}

// Mounts only while open. Numeric fields are written to DOM refs to avoid
// reconciling per-participant subtrees every second; codec/path live in
// React state since they change rarely.
export function CallConnectionStats({
  room,
  remoteParticipants,
  identityToLabel,
  onClose,
}: CallConnectionStatsProps) {
  const [path, setPath] = useState<ConnectionPath>('unknown');
  const [perParticipantStable, setPerParticipantStable] = useState<Record<string, { audioCodec?: string; videoCodec?: string }>>({});
  const [localStable, setLocalStable] = useState<{ audioCodec?: string; videoCodec?: string }>({});

  const serverInfo = useMemo(() => describeServerUrl(getEngine(room)?.url ?? null), [room]);

  // Refs into the rendered DOM for the high-churn numeric fields. Keyed by
  // identity:kind:field. Writing here bypasses React entirely.
  const numericRefs = useRef<Map<string, HTMLSpanElement | null>>(new Map());
  const setNumRef = useCallback((key: string) => (el: HTMLSpanElement | null) => {
    if (el) numericRefs.current.set(key, el);
    else numericRefs.current.delete(key);
  }, []);
  const writeNum = (key: string, text: string) => {
    const el = numericRefs.current.get(key);
    if (el && el.textContent !== text) el.textContent = text;
  };

  useEffect(() => {
    let cancelled = false;
    const prevAudioBytes = new Map<string, BytesSample>();
    const prevVideoBytes = new Map<string, BytesSample>();
    let prevLocalAudio: BytesSample | null = null;
    let prevLocalVideo: BytesSample | null = null;

    const tick = async () => {
      if (cancelled) return;
      try {
        const subPath = await readSubscriberPathStats(room);
        if (!cancelled) {
          setPath((p) => (p === subPath.path ? p : subPath.path));
          if (subPath.rttMs != null) writeNum('transport:rtt', `${subPath.rttMs.toFixed(0)} ms`);
          else writeNum('transport:rtt', '—');
        }

        // Pass transport RTT so per-stream rows can fall back when
        // remote-outbound-rtp hasn't landed yet.
        const stableUpdates: Record<string, { audioCodec?: string; videoCodec?: string }> = {};
        for (const p of remoteParticipants) {
          const s = await readParticipantStats(p, prevAudioBytes, prevVideoBytes, subPath.rttMs);
          if (cancelled) return;
          stableUpdates[p.identity] = {
            audioCodec: s.audio?.codec,
            videoCodec: s.video?.codec,
          };
          // Audio numerics
          writeNum(`${p.identity}:audio:rtt`, fmt(s.audio?.rttMs, 0, ' ms'));
          writeNum(`${p.identity}:audio:jit`, fmt(s.audio?.jitterMs, 1, ' ms'));
          writeNum(`${p.identity}:audio:loss`, fmt(s.audio?.lossPct, 1, '%'));
          writeNum(`${p.identity}:audio:br`, fmt(s.audio?.bitrateKbps, 0, ' kbps'));
          // Video numerics
          writeNum(`${p.identity}:video:rtt`, fmt(s.video?.rttMs, 0, ' ms'));
          writeNum(`${p.identity}:video:jit`, fmt(s.video?.jitterMs, 1, ' ms'));
          writeNum(`${p.identity}:video:loss`, fmt(s.video?.lossPct, 1, '%'));
          writeNum(`${p.identity}:video:br`, fmt(s.video?.bitrateKbps, 0, ' kbps'));
          writeNum(
            `${p.identity}:video:res`,
            s.video?.width && s.video?.height ? `${s.video.width}×${s.video.height}` : '—',
          );
        }
        if (!cancelled) {
          setPerParticipantStable((prev) => {
            // Skip the update unless a codec or participant actually changed.
            const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(stableUpdates)]));
            let changed = false;
            const next: typeof prev = {};
            for (const k of keys) {
              const a = prev[k];
              const b = stableUpdates[k];
              if (!b) { changed = true; continue; }
              next[k] = b;
              if (!a || a.audioCodec !== b.audioCodec || a.videoCodec !== b.videoCodec) changed = true;
            }
            return changed ? next : prev;
          });
        }

        // Local pub stats.
        const local = await readLocalPubStats(room, prevLocalAudio, prevLocalVideo);
        if (cancelled) return;
        prevLocalAudio = local.nextAudio;
        prevLocalVideo = local.nextVideo;
        writeNum('local:audio:br', fmt(local.stats.audio?.bitrateKbps, 0, ' kbps'));
        writeNum('local:video:br', fmt(local.stats.video?.bitrateKbps, 0, ' kbps'));
        writeNum(
          'local:video:res',
          local.stats.video?.width && local.stats.video?.height
            ? `${local.stats.video.width}×${local.stats.video.height}`
            : '—',
        );
        setLocalStable((prev) => {
          const next = {
            audioCodec: local.stats.audio?.codec,
            videoCodec: local.stats.video?.codec,
          };
          if (prev.audioCodec === next.audioCodec && prev.videoCodec === next.videoCodec) return prev;
          return next;
        });
      } catch {
        // Quiet — next tick will repopulate; cells stay '—' meanwhile.
      }
    };

    void tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [room, remoteParticipants]);

  return (
    <div
      className="absolute right-2 top-10 z-[10001] w-[340px] max-w-[calc(100vw-1rem)] rounded-lg border border-border bg-popover shadow-xl text-xs"
      data-testid="call-connection-stats"
    >
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-muted/40">
        <div className="flex items-center gap-1.5 font-semibold">
          <Activity className="h-3.5 w-3.5" />
          Connection stats
        </div>
        <button
          onClick={onClose}
          className="p-0.5 rounded hover:bg-accent"
          aria-label="Close stats"
          data-testid="call-stats-close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-3 space-y-3 max-h-[60vh] overflow-y-auto">
        {/* Path is also shown per-row below; LiveKit uses one subscriber PC,
            so the bucket is shared, but per-row makes lag reports unambiguous. */}
        <div className="space-y-1">
          <div className="text-[10px] text-muted-foreground leading-relaxed">
            Server: <span className="text-foreground">{serverInfo.host}</span>
            {' · '}
            <span>{serverInfo.region}</span>
            {' · transport RTT '}
            <span ref={setNumRef('transport:rtt')}>—</span>
          </div>
        </div>

        {/* Local publish */}
        <div className="space-y-1 border-t border-border pt-2">
          <div className="font-medium text-foreground/90">Your publish</div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
            <div className="text-muted-foreground">Audio</div>
            <div>
              <span ref={setNumRef('local:audio:br')}>—</span>
              {localStable.audioCodec ? <span className="text-muted-foreground"> · {localStable.audioCodec}</span> : null}
            </div>
            <div className="text-muted-foreground">Video</div>
            <div>
              <span ref={setNumRef('local:video:br')}>—</span>
              {' · '}
              <span ref={setNumRef('local:video:res')}>—</span>
              {localStable.videoCodec ? <span className="text-muted-foreground"> · {localStable.videoCodec}</span> : null}
            </div>
          </div>
        </div>

        {/* Per-participant */}
        {remoteParticipants.length === 0 ? (
          <div className="text-muted-foreground text-[11px] border-t border-border pt-2">
            No remote participants yet.
          </div>
        ) : (
          remoteParticipants.map((p) => {
            const label = identityToLabel ? identityToLabel(p.identity) : p.identity;
            const stable = perParticipantStable[p.identity] ?? {};
            return (
              <div
                key={p.sid}
                className="space-y-1 border-t border-border pt-2"
                data-testid={`call-stats-participant-${p.identity}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-foreground/90 truncate">{label}</div>
                  <div
                    className={cn('flex items-center gap-1 text-[10px] font-medium', pathColour(path))}
                    data-testid={`call-stats-path-${p.identity}`}
                  >
                    <span className={cn('inline-block h-1.5 w-1.5 rounded-full', pathDot(path))} />
                    {pathLabel(path)}
                  </div>
                </div>
                <div className="grid grid-cols-[40px_1fr_1fr_1fr_1fr] gap-x-1 gap-y-0.5 items-baseline text-[11px]">
                  <div className="text-muted-foreground"></div>
                  <div className="text-muted-foreground">RTT</div>
                  <div className="text-muted-foreground">Jitter</div>
                  <div className="text-muted-foreground">Loss</div>
                  <div className="text-muted-foreground">Bitrate</div>

                  <div className="text-muted-foreground">Audio</div>
                  <div><span ref={setNumRef(`${p.identity}:audio:rtt`)}>—</span></div>
                  <div><span ref={setNumRef(`${p.identity}:audio:jit`)}>—</span></div>
                  <div><span ref={setNumRef(`${p.identity}:audio:loss`)}>—</span></div>
                  <div><span ref={setNumRef(`${p.identity}:audio:br`)}>—</span></div>

                  <div className="text-muted-foreground">Video</div>
                  <div><span ref={setNumRef(`${p.identity}:video:rtt`)}>—</span></div>
                  <div><span ref={setNumRef(`${p.identity}:video:jit`)}>—</span></div>
                  <div><span ref={setNumRef(`${p.identity}:video:loss`)}>—</span></div>
                  <div><span ref={setNumRef(`${p.identity}:video:br`)}>—</span></div>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Codec: audio {stable.audioCodec ?? '—'} · video {stable.videoCodec ?? '—'}
                  {' · '}res <span ref={setNumRef(`${p.identity}:video:res`)}>—</span>
                </div>
              </div>
            );
          })
        )}

        <div className="border-t border-border pt-2 text-[10px] text-muted-foreground leading-relaxed">
          <div className="font-medium text-foreground/80 mb-0.5">Why is my call laggy?</div>
          The two most common causes are <span className="text-red-600 dark:text-red-400 font-medium">TURN-over-TCP</span> fallback
          (a network/firewall is blocking UDP — try a different network) and
          a server region far from one of the participants. Restarting the
          call sometimes picks a better path.
        </div>
      </div>
    </div>
  );
}
